import { SceneNode } from '@/features/screenplay/components/editor/nodes/SceneNode'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { LexicalEditor } from 'lexical'
import { Shot } from '../store/useShotListStore'


// Helper to format shot data into HTML rows
const generateShotRows = (shots: Shot[]) => {
    if (!shots || shots.length === 0) {
        return `<tr><td colspan="6" style="padding: 20px; text-align: center; color: #a0aec0; font-style: italic;">No shots generated for this scene.</td></tr>`
    }

    return shots.map((shot, index) => {
        const isEven = index % 2 === 0
        const bgColor = isEven ? '#ffffff' : '#f8f9fa'

        return `
        <tr style="background: ${bgColor}; border-bottom: 1px solid #e9ecef; transition: background 0.2s;">
            <td style="padding: 12px 8px; font-weight: 700; color: #667eea; font-size: 11px;">${index + 1}</td>
            <td style="padding: 12px 8px; font-size: 10px;">
                <div style="font-weight: 700; margin-bottom: 3px; color: #2d3748;">${shot.shotType}</div>
                <div style="color: #718096; font-size: 9px; line-height: 1.4;">
                    ${[shot.cameraAngle, shot.cameraMovement].filter(Boolean).join(' • ')}
                </div>
            </td>
            <td style="padding: 12px 8px; color: #4a5568; font-weight: 500;">${shot.subject || '—'}</td>
            <td style="padding: 12px 8px; color: #2d3748; line-height: 1.5;">${shot.description || ''}</td>
            <td style="padding: 12px 8px; color: #4a5568; font-size: 9px; line-height: 1.4;">${shot.emotionalPurpose || '—'}</td>
            <td style="padding: 12px 8px; text-align: center; color: #667eea; font-weight: 600; font-size: 10px;">${shot.duration || '—'}</td>
        </tr>
    `}).join('')
}

const generateHTMLContent = (title: string, scenesData: { sceneName: string; shots: Shot[] }[]) => {
    const date = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })

    // Create sections for each scene
    const scenesHTML = scenesData.map((data, sceneIndex) => `
        <div style="margin-bottom: 35px; page-break-inside: avoid;">
            <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 14px 18px; margin-bottom: 18px; border-radius: 8px; box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);">
                <div style="display: flex; align-items: center; justify-content: space-between;">
                    <h2 style="margin: 0; font-size: 17px; font-weight: 700; color: #ffffff; letter-spacing: 0.3px;">
                        ${data.sceneName.replace(/^\d+\.\s*/, '')}
                    </h2>
                    <span style="font-size: 11px; color: rgba(255, 255, 255, 0.9); background: rgba(255, 255, 255, 0.2); padding: 4px 10px; border-radius: 12px; font-weight: 600;">
                        ${data.shots?.length || 0} Shot${data.shots?.length !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>
            
            <table style="width: 100%; border-collapse: separate; border-spacing: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 10px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); border-radius: 6px; overflow: hidden;">
                <thead>
                    <tr style="background: linear-gradient(to right, #f8f9fa, #e9ecef); color: #495057; text-align: left;">
                        <th style="padding: 10px 8px; width: 35px; font-weight: 700; border-bottom: 2px solid #dee2e6;">#</th>
                        <th style="padding: 10px 8px; width: 110px; font-weight: 700; border-bottom: 2px solid #dee2e6;">Shot Details</th>
                        <th style="padding: 10px 8px; width: 85px; font-weight: 700; border-bottom: 2px solid #dee2e6;">Subject</th>
                        <th style="padding: 10px 8px; font-weight: 700; border-bottom: 2px solid #dee2e6;">Description</th>
                        <th style="padding: 10px 8px; width: 95px; font-weight: 700; border-bottom: 2px solid #dee2e6;">Purpose</th>
                        <th style="padding: 10px 8px; width: 50px; font-weight: 700; border-bottom: 2px solid #dee2e6; text-align: center;">Time</th>
                    </tr>
                </thead>
                <tbody>
                    ${generateShotRows(data.shots)}
                </tbody>
            </table>
        </div>
    `).join('')

    return `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; color: #1a202c; line-height: 1.6; padding: 35px 30px; background: #ffffff;">
            <!-- Header with Branding -->
            <div style="text-align: center; margin-bottom: 40px; padding-bottom: 25px; border-bottom: 2px solid #e2e8f0;">
                <h1 style="margin: 0 0 12px 0; font-size: 26px; font-weight: 800; color: #1a202c; letter-spacing: -0.5px;">
                    ${title}
                </h1>
                <div style="display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 8px;">
                    <span style="color: #718096; font-size: 11px; text-transform: uppercase; letter-spacing: 1.2px; font-weight: 600;">Shot List</span>
                    <span style="color: #cbd5e0; font-size: 11px;">•</span>
                    <span style="color: #718096; font-size: 11px;">${date}</span>
                </div>
                <div style="margin-top: 12px;">
                    <span style="font-size: 10px; font-weight: 600; color: #a0aec0; letter-spacing: 0.5px;">
                        STORYVERSE
                    </span>
                </div>
            </div>
            
            <!-- Scenes Content -->
            ${scenesHTML}
            
            <!-- Footer -->
            <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center;">
                <p style="margin: 0; color: #cbd5e0; font-size: 9px; font-weight: 500;">
                    Generated by <span style="font-weight: 600; color: #a0aec0;">Storyverse</span>
                </p>
            </div>
        </div>
    `
}

export const downloadShotListPDF = async (
    projectTitle: string,
    allScenes: SceneNode[],
    shotLists: Record<string, Shot[]>,
    editor: LexicalEditor,
    options: { type: 'whole' | 'single'; sceneId?: string }
) => {
    // Filter scenes based on options
    let targetScenes = allScenes

    if (options.type === 'single' && options.sceneId) {
        targetScenes = allScenes.filter(s => s.id === options.sceneId)
    }

    if (!targetScenes || targetScenes.length === 0) {
        console.warn("No scenes found to export.")
        return
    }

    // Map scenes to data structure
    const scenesData = targetScenes.map(scene => {
        const shots = shotLists[scene.id] || []
        // Use utility to extract scene header text safely
        const sceneName = editor.read(() => getSceneHeader(scene))
        return {
            sceneName,
            shots
        }
    }).filter(item => options.type === 'single' || item.shots.length > 0)

    if (scenesData.length === 0) {
        alert("No shots found in the selected scope.")
        return
    }

    // Generate HTML String
    console.log("Generating HTML content for PDF...")
    const htmlContent = generateHTMLContent(projectTitle, scenesData)
    console.log("HTML length:", htmlContent.length)

    // Configure PDF options
    const opt = {
        margin: [10, 10, 10, 10], // top, left, bottom, right
        filename: `${projectTitle.replace(/\s+/g, '_')}_ShotList.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    // Generate
    try {
        console.log("Starting html2pdf save...")
        const html2pdf = (await import('html2pdf.js')).default
        await html2pdf().set(opt).from(htmlContent).save()
        console.log("html2pdf save completed.")
    } catch (err) {
        console.error("PDF Generation failed:", err)
        alert("Failed to generate PDF. Check console.")
    }
}
